import React, { useEffect, useState } from "react";

import { css, CSSProperties, keyframes } from "@compiled/react";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Picture, PictureImageProps } from "@/components/wpl/image/src";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { Row } from "@/libraries/wpl/foundations/src";
import {
    ProductKeyTypes,
    TypographyStyles
} from "@/libraries/wpl/foundations/src";
import { Cross, DataEventProps } from "@/libraries/wpl/foundations/src";
import { getLocalStorage, setLocalStorage } from "@/utils/localstorage";

export interface EnhancedBannerProps {
    identifier: string;
    text: React.ReactNode;
    mobileText: React.ReactNode;
    mobileTextThirdScreen?: React.ReactNode;
    linkUrl: string;
    linkText: string;
    productKey: ProductKeyTypes;
    dataEventProps?: DataEventProps;
    imageProps: PictureImageProps;
    imageMobileProps?: PictureImageProps;
    bannerType?: "default" | "lifted";
    customCSS?: CSSProperties;
    backgroundPreset?: "example" | "team25" | "gwp-5090-banners";
}

export const EnhancedBannerAlert = (bannerInfo: EnhancedBannerProps) => {
    const bannerStorageItem = `BannerAlert_dismiss-${bannerInfo.identifier}`;

    const closeIconDataEventProps: DataEventProps = {
        "data-product-key": bannerInfo.productKey,
        "data-event-component": "button",
        "data-event-container": "announcementBanner",
        "data-event": "clicked",
        "data-label": "Close",
        "data-label-english": "Close"
    };

    const [isHidden, setIsHidden] = useState<boolean>(true);
    const mobileClass = bannerInfo.mobileTextThirdScreen
        ? "three-screen"
        : "two-screen";

    useEffect(() => {
        const dismissValue = getLocalStorage(bannerStorageItem);

        if (dismissValue !== "1") {
            setIsHidden(false);
        }
    }, [bannerStorageItem]);

    let mobileProps = bannerInfo.imageProps;
    if (bannerInfo.imageMobileProps && bannerInfo.imageMobileProps.src) {
        mobileProps = bannerInfo.imageMobileProps;
    }

    const bannerBgPreset = bannerInfo.backgroundPreset
        ? `bg-${bannerInfo.backgroundPreset}`
        : `bg-none`;
    const bannerType = bannerInfo.bannerType
        ? `banner-type-${bannerInfo.bannerType}`
        : "banner-type-default";
    const bgLinkColor =
        bannerInfo.backgroundPreset == "gwp-5090-banners"
            ? "bgBlackBlue"
            : "bgWhite";

    return (
        <TypographyStyles>
            <div
                className={`${bannerBgPreset} ${bannerType}`}
                css={[containerStyles]}
                style={bannerInfo.customCSS}
                data-dismissed={isHidden ? "true" : undefined}
                data-testid="banner-alert-container"
            >
                <Row>
                    <>
                        <div
                            css={desktopContainerStyles}
                            className="desktop-container"
                        >
                            <div css={desktopLogoStyles}>
                                <Picture
                                    image={{
                                        width: 209,
                                        ...bannerInfo.imageProps
                                    }}
                                    style={{
                                        maxWidth: "100%",
                                        height: "auto"
                                    }}
                                    forceEagerLoad={true}
                                />
                            </div>
                            <div css={lineStyle} className="line" />
                            <div css={contentStyles}>
                                <p css={pStyle} className="body-md">
                                    {bannerInfo.text}
                                </p>
                                <Link
                                    bgColor={bgLinkColor}
                                    text={bannerInfo.linkText}
                                    href={bannerInfo.linkUrl}
                                    type="primary"
                                    dataEventProps={bannerInfo.dataEventProps}
                                />
                            </div>
                        </div>
                        <div css={mobileContainerStyles}>
                            <div css={marqueeContainerStyles}>
                                <div
                                    className={mobileClass}
                                    css={[marqueeItemStyle, marqueeItem1Styles]}
                                >
                                    <div>
                                        <Picture
                                            image={{
                                                width: 180,
                                                ...mobileProps
                                            }}
                                            style={{
                                                maxWidth: "100%",
                                                height: "auto"
                                            }}
                                            forceEagerLoad={true}
                                        />
                                    </div>
                                </div>
                                <div
                                    className={mobileClass}
                                    css={[marqueeItemStyle, marqueeItem2Styles]}
                                >
                                    <p css={pStyle} className="body-md">
                                        {bannerInfo.mobileText}
                                    </p>
                                </div>

                                {bannerInfo.mobileTextThirdScreen && (
                                    <div
                                        className={mobileClass}
                                        css={[
                                            marqueeItemStyle,
                                            marqueeItem3Styles
                                        ]}
                                    >
                                        <p css={pStyle} className="body-md">
                                            {bannerInfo.mobileTextThirdScreen}
                                        </p>
                                    </div>
                                )}
                            </div>
                            <Link
                                bgColor={bgLinkColor}
                                text={bannerInfo.linkText}
                                href={bannerInfo.linkUrl}
                                type="primary"
                                dataEventProps={bannerInfo.dataEventProps}
                            />
                        </div>
                    </>
                </Row>
                {bannerInfo.bannerType != "lifted" && (
                    <button
                        className={`close-button-symbol`}
                        data-testid="close-button"
                        aria-label="Close"
                        css={[closeStyles]}
                        onClick={() => {
                            setIsHidden(true);
                            setLocalStorage(bannerStorageItem, "1");
                        }}
                        {...closeIconDataEventProps}
                    >
                        <Cross size="24" />
                    </button>
                )}
            </div>
        </TypographyStyles>
    );
};

const scrollUpTwoScreens = keyframes({
    "0%": {
        opacity: "1",
        transform: "translateY(50%)"
    },
    "2%": {
        transform: "translateY(0)"
    },
    "49%": {
        opacity: "1"
    },
    "50%": {
        opacity: "0"
    },
    "100%": {
        opacity: "0"
    }
});

const scrollUpThreeScreens = keyframes({
    "0%": {
        opacity: "1",
        transform: "translateY(50%)"
    },
    "2%": {
        opacity: "1",
        transform: "translateY(0)"
    },
    "32%": {
        opacity: "1"
    },
    "33%, 100%": {
        opacity: "0"
    }
});

const desktopContainerStyles = css({
    paddingBlock: token("space.200"),
    display: "flex",
    flexDirection: "row",
    gap: "32px",
    position: "relative",
    marginBlockEnd: "-2px",
    [media.below.sm]: {
        display: "none"
    }
});

const mobileContainerStyles = css({
    paddingBlock: token("space.200"),
    display: "none",
    flexDirection: "column",
    alignItems: "center",
    [media.below.sm]: {
        display: "flex"
    }
});

const desktopLogoStyles = css({
    display: "flex",
    alignItems: "center"
});

const marqueeContainerStyles = css({
    display: "grid",
    maxWidth: "100%",
    paddingInline: "16px"
});

const marqueeItemStyle = css({
    "gridRow": 1,
    "gridColumn": 1,
    "animationTimingFunction": "linear",
    "animationIterationCount": "infinite",
    "&.two-screen": {
        animationName: `${scrollUpTwoScreens}`,
        animationDuration: "6s"
    },
    "&.three-screen": {
        animationName: `${scrollUpThreeScreens}`,
        animationDuration: "9s",

        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        strong: {
            display: "block"
        }
    }
});

const marqueeItem1Styles = css({
    margin: "0 auto"
});

const marqueeItem2Styles = css({
    textAlign: "center",
    marginBlock: "auto",
    opacity: 0,
    animationDelay: "3s"
});

const marqueeItem3Styles = css({
    textAlign: "center",
    marginBlock: "auto",
    opacity: 0,
    animationDelay: "6s"
});

const containerStyles = css({
    backgroundColor: wacToken("color.extended.blue100"),
    position: "relative",
    padding: 0,
    margin: 0,
    display: "flex",
    zIndex: 1,
    paddingBlockEnd: "2px",
    borderWidth: "0 0 2px 0",
    borderStyle: "solid",
    borderImage: `linear-gradient(90deg, ${wacToken(
        "color.extended.purple500"
    )} 10%, ${wacToken("color.extended.blue900")} 15.63%, ${wacToken(
        "color.extended.blue700"
    )} 33.27%, ${wacToken("color.secondary.red")} 40.62%, ${wacToken(
        "color.primary.yellow"
    )} 63.28%, ${wacToken("color.secondary.green")} 78.23%, ${wacToken(
        "color.primary.lime"
    )}) 1`,

    [`&[data-dismissed="true"]`]: {
        display: "none"
    },
    [`&.banner-type-lifted`]: {
        width: "1064px",
        padding: "0 12px",
        maxWidth: "95%",
        marginInlineStart: "auto",
        marginInlineEnd: "auto",
        borderRadius: "20px",
        [media.below.lg]: {
            maxWidth: "90%",
            padding: "0"
        },
        [media.below.md]: {
            padding: "0"
        }
    },

    [`&.bg-gwp-5090-banners`]: {
        backgroundColor: wacToken("color.extended.blue700"),
        backgroundImage: `url("https://dam-cdn.atl.orangelogic.com/AssetLink/l18iw0nq2hk386041t36c81qqjv8004n.webp"), url("https://dam-cdn.atl.orangelogic.com/AssetLink/2y67r8laua2hf5v04y3302ckn01w336f.webp")`,
        backgroundRepeat: "no-repeat",
        borderStyle: "none",
        backgroundPosition: "0 0, 100% 0",
        backgroundSize: "70px 100%, 70px 100%",
        color: wacToken("color.neutral.light0"),
        [media.above.lg]: {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            [`.row-layout`]: {
                paddingInlineStart: token("space.300"),
                paddingInlineEnd: token("space.300")
            }
        },
        [media.below.lg]: {
            backgroundSize: "60px 100%, 60px 100%"
        },
        [media.below.sm]: {
            backgroundSize: "48px 100%, 60px 100%"
        },
        [media.below.xs]: {
            backgroundSize: "32px 100%, 50px 100%"
        },
        [`&.banner-type-default`]: {
            [media.above.lg]: {
                backgroundImage: `url("https://dam-cdn.atl.orangelogic.com/AssetLink/hf35w2x8d52yd02es6a8k3212njk17nh.webp"),
                url("https://dam-cdn.atl.orangelogic.com/AssetLink/3qp7881jcxw70u3fvei6ki81e71sd5br.webp")`,
                backgroundSize: "130px 100%, 130px 100%"
            }
        },

        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`a, p`]: {
            color: wacToken("color.neutral.light0"),
            [`:visited`]: {
                color: wacToken("color.neutral.light0")
            },
            [`:hover`]: {
                color: wacToken("color.extended.blue100")
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            [`svg path`]: {
                fill: wacToken("color.neutral.light0")
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            [`span`]: {
                fontWeight: "600"
            }
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`strong`]: {
            display: "block",
            fontSize: "1.25rem",
            margin: "0 0 4px",
            [media.below.md]: {
                fontSize: "1.125rem"
            }
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.line`]: {
            borderColor: "transparent"
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.close-button-symbol svg`]: {
            color: wacToken("color.primary.black")
        }
    },

    [`&.bg-team25`]: {
        backgroundImage: `url("https://dam-cdn.atl.orangelogic.com/AssetLink/0nb84k4t5a368nfx6k053baodt3sujub.svg"),
        url("https://dam-cdn.atl.orangelogic.com/AssetLink/52x417io0402tq37716r7abw330lbwb8.svg")`,
        backgroundRepeat: "no-repeat",
        backgroundSize:
            "var(--eb-bg-size-1, auto 100%), var(--eb-bg-size-2, auto 100%)",
        backgroundPosition:
            "var(--eb-bg-pos-1, -64px 0px), var(--eb-bg-pos-2, right -54px top 0px)",
        [media.only.sm]: {
            ["--eb-bg-pos-2"]: "right -64px top 0px"
        },
        [media.only.md]: {
            ["--eb-bg-pos-1"]: "-44px 0px",
            ["--eb-bg-pos-2"]: "right -44px top 0px",
            ["--eb-bg-size-2"]: "auto 112px"
        },
        [media.above.lg]: {
            ["--eb-bg-size-1"]: "contain",
            ["--eb-bg-size-2"]: "contain",
            ["--eb-bg-pos-1"]: "0% 0%",
            ["--eb-bg-pos-2"]: "100% 0%"
        },

        // contrasting close-button color
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        [`.close-button-symbol svg`]: {
            color: wacToken("color.neutral.light0")
        }
    },
    [`&.bg-example`]: {
        // background preset
    }
});

const contentStyles = css({
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "32px",

    [media.below.md]: {
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "5px"
    },

    [media.below.sm]: {
        alignItems: "center"
    },

    // Targeting link styling
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        whiteSpace: "nowrap"
    }
});

const pStyle = css({
    margin: "0",
    zIndex: 1
});

const lineStyle = css({
    borderInlineStart: `1px solid ${wacToken("color.neutral.light700")}`,
    height: "100%",

    [media.below.sm]: {
        display: "none"
    }
});

const closeStyles = css({
    border: 0,
    appearance: "none",
    background: "transparent",
    cursor: "pointer",
    padding: 0,
    position: "absolute",
    zIndex: 1,
    insetInlineEnd: 0,
    margin: "16px"
});
